import { useCallback, useEffect, useState } from "react";
import { addDays, addMonths, addQuarters, addWeeks, addYears, differenceInDays, format } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import { ExerciseSaleSearchParams } from "../../../models/ExerciseSaleSearchParams";
import { DateSearchParam } from "../../../utils/dateSearchParam";
import { State } from "../../../store";
import useApiRequest from "../../../utils/hook/useApiRequest";
import { GET_EXERCISE_BY_DATE, GET_TICKET_BY_EXERCISEGROUP } from "../../../api/apiRoutes";
import { ExerciseSaleModel } from "../../../models/ExerciseSaleModel";
import { addTicketWithExerciseToBasket, clearSelectExercise, setActiveStep } from "../../../store/basket/basketActions";
import { RouteComponentProps } from "react-router";
import { setErrors } from "../../../store/alerts/alertsActions";
import { Assort } from "../../../models/Assort";
import { TicketBillLine } from "../../../models/TicketBillLine";
import useWithDispatch from "../../../utils/hook/withDispatchHook";
import { TENIS_TYPE } from "../../MainPage";

const formatDate = (date: number) => {
  return format(new Date(date), "yyyy-MM-dd");
};

const mappingExercisesSaleModel = (value: ExerciseSaleModel[], searchParams: ExerciseSaleSearchParams) => {
  const diffDay = differenceInDays(getDateFrom(searchParams), new Date(searchParams.dateFrom));
  let exercisesSaleList: ExerciseSaleModel[] = [];
  for (let i = 0; i <= diffDay; i++) {
    const list: ExerciseSaleModel[] = value
      .map((item) => ({
        ...item,
        exercises: item.exercises.filter(
          (exercise) => formatDate(exercise.startTime) === formatDate(addDays(searchParams.dateFrom, i).getTime())
        ),
      }))
      .filter((item) => item.exercises.length > 0);

    if (list.length > 0) {
      exercisesSaleList = exercisesSaleList.concat(list);
    }
  }

  return exercisesSaleList;
};

const filterExerciseByTenis = (tenisType: string, exerciseList: ExerciseSaleModel[]) => {
  if (tenisType === "hala") {
    return exerciseList.filter((item) => item.name.toLowerCase().search("hala") > -1);
  } else {
    return exerciseList.filter((item) => item.name.toLowerCase().search("hala") < 0);
  }
};

const getDateFrom = (searchParams: ExerciseSaleSearchParams) => {
  const { dateFrom, dateTo, dateSearchParam } = searchParams;
  if (dateSearchParam === DateSearchParam.nearestWeek) {
    return addWeeks(dateFrom, 1);
  }
  if (dateSearchParam === DateSearchParam.nearestMonth) {
    return addMonths(dateFrom, 1);
  }
  if (dateSearchParam === DateSearchParam.nearestQuartet) {
    return addQuarters(dateFrom, 1);
  }
  if (dateSearchParam === DateSearchParam.nearestYear) {
    return addYears(dateFrom, 1);
  }
  return new Date(dateTo);
};

const isBasket = (basket: TicketBillLine[], tempId: string) => {
  return basket.some((item) => item.tempId === tempId);
};

const validateBasket = (basket: TicketBillLine[], tempId: string, exerciseMaxQnt: number) => {
  const item = basket.filter((item) => item.tempId === tempId)[0];
  return item.quantity === exerciseMaxQnt;
};

export default function useExerciseSale(props: RouteComponentProps) {
  const [loading, setLoading] = useState(false);
  const [exercisesSale, setExercisesSale] = useState<ExerciseSaleModel[]>([]);
  const searchParams = useSelector((state: State) => state.exerciseSaleFilters.searchParams);
  const { post } = useApiRequest();
  const dispatch = useDispatch();
  const exerciseSelect = useSelector((state: State) => state.basket.exerciseSelect);
  const [ticketList, setTicketList] = useState<{ tickets: Assort[] }[]>([]);
  const basket = useSelector((state: State) => state.basket.basket);
  const withDispatch = useWithDispatch();
  const tenisType = sessionStorage.getItem(TENIS_TYPE);

  const handleGetExercises = useCallback(async () => {
    setLoading(true);
    const { isError, result } = await post(GET_EXERCISE_BY_DATE, { ...searchParams, internet: true });
    if (!isError) {
      if (tenisType != null) {
        setExercisesSale(mappingExercisesSaleModel(filterExerciseByTenis(JSON.parse(tenisType), result), searchParams));
      } else {
        setExercisesSale(mappingExercisesSaleModel(result, searchParams));
      }
    }
    setLoading(false);
  }, [post, searchParams]);

  useEffect(
    () => {
      handleGetExercises();
    }, // eslint-disable-next-line
    [handleGetExercises]
  );

  useEffect(
    () => {
      dispatch(clearSelectExercise);
      dispatch(setActiveStep(0));
    }, // eslint-disable-next-line
    []
  );



  const getTicket = async () => {
    setLoading(true);
    const promises = exerciseSelect.map((item) =>
      post(GET_TICKET_BY_EXERCISEGROUP, {
        objectDb: item.exerciseGroup.objectDb,
        objectId: item.exerciseGroup.objectId,
      })
    );
    const tickets = await Promise.all(promises);
    setTicketList(
      tickets.map((response) => ({
        tickets: response.result,
      }))
    );
    setLoading(false);
  };

  const addTicket = (ticket: TicketBillLine) => {
    if (isBasket(basket, ticket.tempId)) {
      if (
        validateBasket(
          basket,
          ticket.tempId,
          ticket.ticketAccessList[0].exercise.capacity - ticket.ticketAccessList[0].exercise.reserved
        )
      ) {
        withDispatch(setErrors)(["Brak miejsc na wybrany termin"]);
      } else {
        withDispatch(addTicketWithExerciseToBasket)(ticket);
      }
    } else {
      withDispatch(addTicketWithExerciseToBasket)(ticket);
    }
  };

  return {
    loading,
    exercisesSale,
    searchParams,
    ticketList,
    getTicket,
    addTicket,
  };
}
